import React, { useEffect, useState } from "react";
import { Button } from "../../../components/ui";
import {
  CAROUSEL_WINDOW_DAYS,
  appointmentTimeLoud,
  durationLabel,
  linkLabel,
  whenLabel,
} from "../appointments";
import type { ClientAppointment } from "../types/appointments.types";

/**
 * How long a slide is held before the next one takes its place.
 *
 * Six seconds. A slide is two short lines and a date, which is read in about
 * two — the rest is the pause somebody needs to decide whether this is the
 * appointment they were looking for before it is taken away from them.
 */
const SLIDE_MS = 6000;

/**
 * How long the track takes to carry the next slide in, and the curve it does
 * it on.
 *
 * 450ms and an ease-out: long enough that the eye follows the card across
 * rather than being handed a different one, short enough that somebody
 * pressing a dot is not made to wait for it. The curve leaves quickly and
 * settles slowly, which is what makes it read as a thing coming to rest
 * instead of a position being assigned.
 */
const GLIDE = "transform 450ms cubic-bezier(0.22, 0.61, 0.36, 1)";

/** Whether the reader has asked for less movement. */
const wantsStillness = (): boolean =>
  !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * The same answer, kept current.
 *
 * Subscribed rather than read once, because it now governs two things: whether
 * the strip advances on its own, and whether it slides when it does. Content
 * that moves by itself is what that setting is most plainly about, so for
 * anybody who has turned it on the carousel neither advances nor animates —
 * pressing a dot simply puts the next card there. Every slide stays reachable.
 */
const useStillness = (): boolean => {
  const [still, setStill] = useState(wantsStillness);

  useEffect(() => {
    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!query) return;

    const read = () => setStill(query.matches);
    read();
    query.addEventListener?.("change", read);
    return () => query.removeEventListener?.("change", read);
  }, []);

  return still;
};

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export interface AppointmentCarouselProps {
  /**
   * The appointments to page through — every one still standing inside the
   * next week, soonest first. See `upcomingSoon`, which is what picks them;
   * this draws whatever it is given, in the order it is given.
   */
  appointments: ClientAppointment[];
  /** Opens the reschedule dialog on the slide showing. */
  onReschedule: (appointment: ClientAppointment) => void;
  /** Opens the cancel dialog on the slide showing. */
  onCancel: (appointment: ClientAppointment) => void;
}

/**
 * The week ahead, one appointment at a time.
 *
 * A carousel rather than a list because of where it sits: directly above the
 * table, which is itself a list of the same rows. Stacking four cards over a
 * table of four rows would push the table off the screen to say twice what it
 * already says. One card at a time is a reminder; the table behind it is the
 * record.
 *
 * Seven days because that is the horizon somebody opening a client is working
 * to — what is happening with this person this week. Anything further out is
 * in the table under the Upcoming tab, which is the right place for it.
 *
 * Cancelled appointments are not here. See `upcomingSoon`.
 */
const AppointmentCarousel: React.FC<AppointmentCarouselProps> = ({
  appointments,
  onReschedule,
  onCancel,
}) => {
  const [index, setIndex] = useState(0);
  /**
   * Whether the pointer is over the strip, or something inside it has focus.
   *
   * The two acts the card offers — calling the appointment off, moving it —
   * are aimed at *this* slide, and a strip that changed under a reaching
   * pointer would cancel the wrong one. So approaching it stops it.
   */
  const [held, setHeld] = useState(false);
  const still = useStillness();
  /**
   * Set for the one frame in which the track jumps back to the start without
   * animating — see `rendered` and `handleGlideEnd` for what that is for.
   */
  const [snapping, setSnapping] = useState(false);

  const count = appointments.length;
  /** Where the copy of the first slide sits: one past the last real one. */
  const clonePosition = count;

  // The list moves under this: cancelling the slide showing drops it out, and
  // the index it was at can end up past the end. Clamped rather than reset, so
  // cancelling the third of four leaves the reader on the last one rather than
  // snapping them back to the first.
  useEffect(() => {
    setIndex((current) =>
      current > count - 1 ? Math.max(count - 1, 0) : current
    );
  }, [count]);

  /**
   * Moves to the next slide, and onto the copy of the first from the last.
   *
   * `current + 1` with no wrap, deliberately. Taking the index back to 0 from
   * the last slide is what made the strip rewind: the track would travel the
   * whole way back across every card in between, which reads as the carousel
   * running backwards rather than coming round. So it keeps going forward onto
   * the copy, and `handleGlideEnd` puts it back on the real first slide once
   * the eye has stopped following it.
   *
   * A `setTimeout` re-armed on every index change rather than one standing
   * `setInterval`: it means a slide chosen by its dot gets the full dwell
   * before the next one arrives, instead of inheriting whatever was left of a
   * tick that had been running since before the press.
   */
  useEffect(() => {
    if (held || count < 2 || still) return;

    const timer = setTimeout(
      () => setIndex((current) => Math.min(current + 1, clonePosition)),
      SLIDE_MS
    );
    return () => clearTimeout(timer);
  }, [index, held, still, count, clonePosition]);

  /**
   * Lets the track animate again, the frame after it was put back.
   *
   * Two frames rather than one: the first is the one the browser paints the
   * jump in, with the transition off. Turning it back on in the same frame
   * would let the browser coalesce the two changes and animate the jump after
   * all, which is the rewind this exists to avoid.
   */
  useEffect(() => {
    if (!snapping) return;

    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setSnapping(false));
    });

    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [snapping]);

  /**
   * The moment the glide onto the copy of the first slide finishes, the track
   * is moved back onto the real one with the animation off.
   *
   * Nothing is seen: the two slides are the same appointment drawn the same
   * way, so the jump is between two identical pictures. What it buys is a
   * track that is back at the start and can glide forward again, which is what
   * makes the strip go round rather than back and forth.
   *
   * Guarded on the target because `transitionend` bubbles — a hover colour
   * finishing on the Reschedule button inside a slide would otherwise be read
   * as the track arriving. That guard is the one doing the work; the property
   * check behind it only matters if the track is ever given a second thing to
   * animate, and it lets a missing `propertyName` through because jsdom has no
   * `TransitionEvent` to put one on.
   */
  const handleGlideEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.propertyName && event.propertyName !== "transform") return;
    if (index !== clonePosition) return;

    setSnapping(true);
    setIndex(0);
  };

  // Nothing in the week ahead is not a state worth drawing a card for — the
  // table below already says what this client's appointments are.
  if (appointments.length === 0) return null;

  // The track never travels past the copy at the end, however far `index` has
  // been left behind by a list that shrank under it.
  const at = Math.min(index, clonePosition);
  /** Which appointment is actually in view — the copy stands for the first. */
  const showingPosition = at % count;

  /**
   * The slides the track carries: every appointment, and then the first one
   * again.
   *
   * That last entry is the whole of how this goes round. Without it the only
   * way back to the start is to move the track the other way, across every
   * card in between — the rewind the strip used to do. With it, coming round
   * is another step forward, and the step back to the real first slide happens
   * with the animation off, between two pictures that are identical.
   *
   * Only added once there is more than one: a single appointment never
   * advances, so a copy of it would be a second card nobody can reach.
   */
  const rendered =
    count > 1 ? [...appointments, appointments[0]] : appointments;

  return (
    <section
      aria-label={`Appointments in the next ${CAROUSEL_WINDOW_DAYS} days`}
      className="flex-shrink-0"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      // `focus` and `blur` do not bubble, but React's synthetic versions do —
      // so tabbing to Cancel, to Reschedule or to a dot stops the strip, and
      // tabbing out of it starts it again.
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      {/* The frame, which holds still. What moves is the track inside it, so a
          reader sees one strip with appointments travelling through it rather
          than a box whose contents are swapped out underneath them.
          `overflow-hidden` is what crops the slides waiting either side. */}
      <div className="overflow-hidden rounded-md border border-[#BFEFF3] bg-[#F2FCFD]">
        <div
          className="flex"
          onTransitionEnd={handleGlideEnd}
          style={{
            // Whole slides, so the arithmetic is the index and nothing else —
            // nothing to measure, and no drift after a resize.
            transform: `translateX(-${at * 100}%)`,
            // Off for the frame the track is put back on, and off entirely for
            // a reader who has asked for less movement.
            transition: still || snapping ? undefined : GLIDE,
          }}
        >
          {rendered.map((appointment, slide) => {
            const start = new Date(appointment.startsAt);
            const joinable =
              appointment.type === "Online" && !!appointment.meetingUrl;
            // Which appointment this is, counted around the copy at the end.
            const position = slide % count;
            const showing = slide === at;

            return (
              <div
                // The copy shares an appointment with the first slide, so it
                // cannot share its key.
                key={slide === clonePosition ? `${appointment.id}-again` : appointment.id}
                // Every slide is the full width of the frame and refuses to be
                // squeezed, which is what keeps one in view at a time and the
                // `-100%` steps exact.
                className="w-full flex-shrink-0 flex flex-wrap items-center gap-x-5 gap-y-4 px-4 sm:px-5 py-4"
                // A labelled group, so the dots and the card agree about which
                // of how many this is when the strip is read out.
                role="group"
                aria-roledescription="slide"
                aria-label={`Appointment ${position + 1} of ${count}`}
                // The slides waiting either side are drawn but are not there:
                // `inert` takes their Cancel and Reschedule out of the tab
                // order and out of the accessibility tree, so the strip cannot
                // be used to call off an appointment nobody can see. The
                // `aria-hidden` beside it says the same to anything that does
                // not implement `inert` yet.
                inert={!showing}
                aria-hidden={!showing || undefined}
              >
                {/* The day as a torn-off calendar leaf. `aria-hidden` because
                    the sentence beside it already says when this is, and a
                    screen reader reading "Sep 24" and then "in 2 days" would
                    be told the date twice in two formats. */}
                <div
                  aria-hidden="true"
                  className="flex h-[54px] w-[54px] flex-shrink-0 flex-col items-center justify-center rounded-[6px] border border-[#BFEFF3] bg-white"
                >
                  <span className="text-[11px] leading-none text-ink-muted">
                    {MONTHS_SHORT[start.getMonth()]}
                  </span>
                  <span className="text-[20px] font-medium leading-tight text-ink">
                    {start.getDate()}
                  </span>
                </div>

                <div className="min-w-[200px] flex-1">
                  <p className="text-[15px] 3xl:text-[16px] font-medium text-ink">
                    {/* Only the first slide is the next one. Calling the third
                        card "Next appointment" as well would make the word
                        meaningless. */}
                    {position === 0 ? "Next appointment" : "Appointment"} ·{" "}
                    {whenLabel(appointment.startsAt)}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] 3xl:text-[14px]">
                    <span className="text-ink-muted">
                      {appointmentTimeLoud(appointment.startsAt)}
                    </span>
                    <span aria-hidden="true" className="text-ink-subtle">
                      •
                    </span>
                    <span className="text-ink-muted">
                      {durationLabel(appointment.durationMinutes)}
                    </span>
                    <span aria-hidden="true" className="text-ink-subtle">
                      •
                    </span>
                    {/* An offline appointment says so where the link would be,
                        the same as the strip above the tabs does. */}
                    {joinable ? (
                      <a
                        href={appointment.meetingUrl!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-[#00A5B4] hover:text-[#007E8C] hover:underline transition-colors break-all"
                      >
                        {linkLabel(appointment.meetingUrl!)}
                      </a>
                    ) : (
                      <span className="text-ink-muted">{appointment.type}</span>
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  {/* Red text rather than a red button: it stands beside
                      Reschedule on a tinted card, and two filled buttons there
                      would read as a pair of equal choices when calling the
                      appointment off is the rarer and heavier one. The dialog
                      behind it is where the weight is. */}
                  <button
                    type="button"
                    onClick={() => onCancel(appointment)}
                    className="rounded-[6px] px-1 text-[14px] font-medium text-[#FF3B30] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF3B30]/40"
                  >
                    Cancel
                  </button>
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => onReschedule(appointment)}
                  >
                    Reschedule
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots rather than arrows: there are rarely more than three or four, and
          a row of dots says how many there are as well as where you are, which
          a pair of chevrons does not. Hidden entirely at one, where they would
          be a control that does nothing. */}
      {count > 1 && (
        <div className="mt-3 flex items-center justify-center gap-2">
          {appointments.map((appointment, slide) => (
            <button
              key={appointment.id}
              type="button"
              aria-label={`Show appointment ${slide + 1} of ${count}`}
              // The copy at the end is the first appointment, so its dot is
              // the first dot — the row must not go blank as the strip comes
              // round.
              aria-current={slide === showingPosition || undefined}
              onClick={() => setIndex(slide)}
              className={`h-2 w-2 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40 ${
                slide === showingPosition
                  ? "bg-[#00BACB]"
                  : "bg-[#D6DEE2] hover:bg-[#B9C5CB]"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default AppointmentCarousel;
